/**
 * FinanceThisCar Component - Enhanced Version with CarMoney-style Calculator
 * Displays finance providers with eligibility matching on listing detail page
 * Includes advanced filters, detailed buyer input matching, and finance calculator
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from './ui/accordion';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useCountry } from '../context/CountryContext';
import { useToast } from '../hooks/use-toast';
import { 
  Banknote, 
  Zap, 
  RefreshCw, 
  Building2, 
  Car,
  ChevronRight,
  ChevronDown,
  Info,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Star,
  Shield,
  Clock,
  Percent,
  BadgeCheck,
  DollarSign,
  Filter,
  X,
  Calculator,
  CreditCard,
  TrendingUp,
  PiggyBank,
  Receipt
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge icon mapping
const BADGE_ICONS = {
  low_interest: Percent,
  quick_approval: Zap,
  flexible_terms: RefreshCw,
  trusted_lender: Building2,
  used_cars_accepted: Car
};

const BADGE_COLORS = {
  low_interest: 'bg-green-100 text-green-800 border-green-200',
  quick_approval: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  flexible_terms: 'bg-blue-100 text-blue-800 border-blue-200',
  trusted_lender: 'bg-purple-100 text-purple-800 border-purple-200',
  used_cars_accepted: 'bg-orange-100 text-orange-800 border-orange-200'
};

const CREDIT_SCORES = [
  { value: 'excellent', label: 'Excellent', description: 'No missed payments, low debt' },
  { value: 'good', label: 'Good', description: 'Minor issues, generally reliable' },
  { value: 'fair', label: 'Fair', description: 'Some late payments or higher debt' },
  { value: 'poor', label: 'Poor', description: 'Multiple issues, limited options' },
  { value: 'not_sure', label: 'Not Sure', description: "We'll use average rates" }
];

const FinanceThisCar = ({ listing, isDemo = false }) => {
  const { country, formatCurrency } = useCountry();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [disclaimer, setDisclaimer] = useState(null);
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [calculatingQuote, setCalculatingQuote] = useState(false);
  const [calculatedQuote, setCalculatedQuote] = useState(null);
  const [dialogTab, setDialogTab] = useState('calculator');
  
  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState({
    interest_level: [],
    deposit_required: [],
    early_repayment_penalty: null,
    approval_time: [],
    online_application: null,
    documents_required: [],
    accepts_used_vehicles: null,
    max_vehicle_age: 15,
    income_types_accepted: [],
    tenure_range: [12, 84],
    provider_type: [],
    is_regulated: null,
    high_approval_likelihood: null
  });
  
  // Calculator inputs
  const [calcInputs, setCalcInputs] = useState({
    deposit: 0,
    tenure_months: 60,
    credit_score: 'good'
  });
  
  // Application form
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    employment_type: 'salaried',
    monthly_income: '',
    existing_loans: 'no'
  });

  // Calculate vehicle data from listing
  const vehicleData = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const vehicleYear = listing?.year || currentYear;
    return {
      price: parseFloat(listing?.price) || 0,
      age: currentYear - vehicleYear,
      is_used: listing?.condition?.toLowerCase() !== 'new'
    };
  }, [listing]);

  // Fetch providers and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [catRes, provRes] = await Promise.all([
          axios.get(`${API_URL}/api/finance/categories`, { params: { country } }),
          axios.get(`${API_URL}/api/finance/providers`, { 
            params: { 
              country, 
              listing_id: listing?.id,
              vehicle_price: vehicleData.price,
              vehicle_age: vehicleData.age,
              is_used: vehicleData.is_used
            }
          })
        ]);
        
        if (catRes.data.success) setCategories(catRes.data.categories);
        if (provRes.data.success) {
          setProviders(provRes.data.providers);
          setDisclaimer(provRes.data.disclaimer);
        }
        
      } catch (error) {
        console.error('Error fetching finance data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (listing?.price) {
      fetchData();
    }
  }, [country, listing, vehicleData]);

  // Refetch with filters when badges change
  useEffect(() => {
    if (!listing?.price || loading) return;
    
    const fetchFiltered = async () => {
      try {
        const params = {
          country,
          listing_id: listing?.id,
          vehicle_price: vehicleData.price,
          vehicle_age: vehicleData.age,
          is_used: vehicleData.is_used
        };
        
        if (selectedBadges.length > 0) {
          params.badges = selectedBadges.join(',');
        }
        
        const response = await axios.get(`${API_URL}/api/finance/providers`, { params });
        if (response.data.success) {
          let filteredProviders = response.data.providers;
          
          // Apply client-side advanced filters
          if (advancedFilters.interest_level.length > 0) {
            filteredProviders = filteredProviders.filter(p => 
              advancedFilters.interest_level.includes(p.interest_level)
            );
          }
          if (advancedFilters.approval_time.length > 0) {
            filteredProviders = filteredProviders.filter(p => 
              advancedFilters.approval_time.includes(p.approval_time)
            );
          }
          if (advancedFilters.provider_type.length > 0) {
            filteredProviders = filteredProviders.filter(p => 
              advancedFilters.provider_type.includes(p.provider_type)
            );
          }
          if (advancedFilters.is_regulated !== null) {
            filteredProviders = filteredProviders.filter(p => 
              p.is_regulated === advancedFilters.is_regulated
            );
          }
          
          setProviders(filteredProviders);
        }
      } catch (error) {
        console.error('Error filtering providers:', error);
      }
    };
    
    fetchFiltered();
  }, [selectedBadges, advancedFilters, country, listing, vehicleData, loading]);

  // Calculate finance quote when inputs change
  const calculateQuote = useCallback(async () => {
    if (!selectedProvider || !vehicleData.price) return;
    
    try {
      setCalculatingQuote(true);
      
      const response = await axios.post(`${API_URL}/api/finance/calculate`, {
        provider_id: selectedProvider.id,
        vehicle_price: vehicleData.price,
        deposit: calcInputs.deposit,
        tenure_months: calcInputs.tenure_months,
        credit_score: calcInputs.credit_score
      });
      
      if (response.data.success) {
        setCalculatedQuote(response.data);
      }
      
    } catch (error) {
      console.error('Error calculating quote:', error);
    } finally {
      setCalculatingQuote(false);
    }
  }, [selectedProvider, vehicleData.price, calcInputs]);

  // Auto-calculate when dialog opens or inputs change
  useEffect(() => {
    if (showApplicationDialog && selectedProvider) {
      calculateQuote();
    }
  }, [showApplicationDialog, selectedProvider, calcInputs, calculateQuote]);

  // Toggle badge filter
  const toggleBadge = (badgeId) => {
    setSelectedBadges(prev => 
      prev.includes(badgeId) 
        ? prev.filter(b => b !== badgeId)
        : [...prev, badgeId]
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedBadges([]);
    setAdvancedFilters({
      interest_level: [],
      deposit_required: [],
      early_repayment_penalty: null,
      approval_time: [],
      online_application: null,
      documents_required: [],
      accepts_used_vehicles: null,
      max_vehicle_age: 15,
      income_types_accepted: [],
      tenure_range: [12, 84],
      provider_type: [],
      is_regulated: null,
      high_approval_likelihood: null
    });
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = selectedBadges.length;
    if (advancedFilters.interest_level.length > 0) count++;
    if (advancedFilters.approval_time.length > 0) count++;
    if (advancedFilters.provider_type.length > 0) count++;
    if (advancedFilters.is_regulated !== null) count++;
    return count;
  }, [selectedBadges, advancedFilters]);

  // Handle application submission
  const handleSubmitApplication = async () => {
    if (!selectedProvider) return;
    
    if (!applicationForm.name || !applicationForm.email || !applicationForm.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await axios.post(`${API_URL}/api/finance/applications`, {
        listing_id: listing?.id,
        vehicle_price: vehicleData.price,
        buyer_name: applicationForm.name,
        buyer_email: applicationForm.email,
        buyer_phone: applicationForm.phone,
        income_type: applicationForm.employment_type,
        monthly_budget: calculatedQuote?.calculation?.monthly_payment || null,
        deposit_amount: calcInputs.deposit,
        preferred_tenure: calcInputs.tenure_months,
        provider_id: selectedProvider.id,
        provider_name: selectedProvider.name,
        country,
        monthly_income: applicationForm.monthly_income || null,
        existing_loans: applicationForm.existing_loans,
        // Include calculation data
        calculated_apr: calculatedQuote?.calculation?.apr,
        calculated_monthly: calculatedQuote?.calculation?.monthly_payment,
        credit_score: calcInputs.credit_score
      });
      
      if (response.data.success) {
        toast({
          title: "Application Submitted!",
          description: `Your finance enquiry has been sent to ${selectedProvider.name}. They will contact you shortly.`,
        });
        setShowApplicationDialog(false);
        setApplicationForm({ 
          name: '', email: '', phone: '', 
          employment_type: 'salaried', monthly_income: '', existing_loans: 'no' 
        });
        setCalculatedQuote(null);
      }
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Open dialog with provider
  const openFinanceDialog = (provider) => {
    setSelectedProvider(provider);
    setCalcInputs({
      deposit: Math.round(vehicleData.price * (provider.min_deposit_percent || 0) / 100),
      tenure_months: 60,
      credit_score: 'good'
    });
    setDialogTab(provider.show_calculator !== false ? 'calculator' : 'apply');
    setShowApplicationDialog(true);
  };

  // Render provider card
  const renderProviderCard = (provider) => {
    const IconComponent = BADGE_ICONS[provider.badges?.[0]] || Building2;
    const eligibilityScore = provider.eligibility_score || 0;
    const isTopMatch = eligibilityScore >= 70;
    
    return (
      <Card 
        key={provider.id} 
        className={`relative overflow-hidden transition-all hover:shadow-md ${
          isTopMatch ? 'border-green-300 bg-green-50/30' : ''
        } ${provider.is_sponsored ? 'border-yellow-300' : ''}`}
        data-testid={`finance-provider-${provider.id}`}
      >
        {provider.is_sponsored && (
          <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-medium px-2 py-0.5 rounded-bl">
            Sponsored
          </div>
        )}
        
        {provider.is_new_partner && !provider.is_sponsored && (
          <div className="absolute top-0 right-0 bg-blue-400 text-white text-xs font-medium px-2 py-0.5 rounded-bl">
            New Partner
          </div>
        )}

        {isDemo && !provider.is_sponsored && !provider.is_new_partner && (
          <div className="absolute top-0 right-0 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold px-2 py-0.5 rounded-bl" data-testid={`finance-provider-demo-${provider.id}`}>
            DEMO
          </div>
        )}
        
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              provider.provider_type === 'bank' ? 'bg-purple-100' :
              provider.provider_type === 'finance_company' ? 'bg-blue-100' :
              'bg-gray-100'
            }`}>
              <IconComponent className={`h-6 w-6 ${
                provider.provider_type === 'bank' ? 'text-purple-600' :
                provider.provider_type === 'finance_company' ? 'text-blue-600' :
                'text-gray-600'
              }`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                {provider.is_verified && <BadgeCheck className="h-4 w-4 text-blue-500" />}
                {provider.is_regulated && <Shield className="h-4 w-4 text-green-500" title="Regulated" />}
              </div>
              
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{provider.description}</p>
              
              <div className="flex flex-wrap gap-1 mt-2">
                {provider.badges?.map(badge => {
                  const BadgeIcon = BADGE_ICONS[badge];
                  const badgeLabel = categories.find(c => c.id === badge)?.label || badge.replace(/_/g, ' ');
                  return (
                    <Badge key={badge} variant="outline" className={`text-xs ${BADGE_COLORS[badge] || ''}`}>
                      {BadgeIcon && <BadgeIcon className="h-3 w-3 mr-1" />}
                      {badgeLabel}
                    </Badge>
                  );
                })}
              </div>
              
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="font-semibold text-gray-900">
                    {provider.interest_rate_min}% - {provider.interest_rate_max}%
                  </div>
                  <div className="text-gray-500">Interest</div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="font-semibold text-gray-900 flex items-center justify-center gap-1">
                    <Clock className="h-3 w-3" />
                    {provider.approval_time}
                  </div>
                  <div className="text-gray-500">Approval</div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="font-semibold text-gray-900">{provider.min_deposit_percent}%</div>
                  <div className="text-gray-500">Min Deposit</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                {provider.online_application && (
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    Online Application
                  </span>
                )}
                {provider.accepts_used_vehicles && (
                  <span className="flex items-center gap-1">
                    <Car className="h-3 w-3 text-blue-500" />
                    Used Cars OK
                  </span>
                )}
              </div>
            </div>
            
            {eligibilityScore > 0 && (
              <div className={`text-center p-2 rounded-lg ${
                eligibilityScore >= 70 ? 'bg-green-100' :
                eligibilityScore >= 40 ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                <div className={`text-2xl font-bold ${
                  eligibilityScore >= 70 ? 'text-green-700' :
                  eligibilityScore >= 40 ? 'text-yellow-700' : 'text-red-700'
                }`}>
                  {Math.round(eligibilityScore)}
                </div>
                <div className="text-xs text-gray-600">Match</div>
              </div>
            )}
          </div>
          
          <Button 
            className="w-full mt-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            onClick={() => openFinanceDialog(provider)}
            data-testid={`apply-finance-${provider.id}`}
          >
            {provider.show_calculator !== false ? 'Calculate & Apply' : 'Apply for Finance'}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Render Calculator Tab Content
  const renderCalculator = () => {
    if (!selectedProvider) return null;
    
    const showCalculator = selectedProvider.show_calculator !== false;
    
    if (!showCalculator) {
      return (
        <div className="text-center py-6">
          <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p className="text-gray-600">This provider will send you a personalized quote after you submit your application.</p>
          <Button 
            className="mt-4"
            onClick={() => setDialogTab('apply')}
          >
            Continue to Application
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {/* Vehicle Price (Read-only) */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Price of car</span>
            <span className="text-xl font-bold text-gray-900">{formatCurrency(vehicleData.price)}</span>
          </div>
        </div>
        
        {/* Deposit Input */}
        <div>
          <Label className="flex justify-between">
            <span>Your deposit</span>
            <span className="text-sm text-gray-500">
              Min: {formatCurrency(vehicleData.price * (selectedProvider.min_deposit_percent || 0) / 100)}
            </span>
          </Label>
          <div className="relative mt-2">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="number"
              className="pl-10 text-lg"
              value={calcInputs.deposit}
              onChange={(e) => setCalcInputs(prev => ({ ...prev, deposit: parseFloat(e.target.value) || 0 }))}
              placeholder="0"
            />
          </div>
          <Slider
            value={[calcInputs.deposit]}
            onValueChange={([v]) => setCalcInputs(prev => ({ ...prev, deposit: v }))}
            min={0}
            max={vehicleData.price * 0.5}
            step={vehicleData.price > 100000 ? 10000 : 100}
            className="mt-2"
          />
        </div>
        
        {/* Tenure Selection */}
        <div>
          <Label>Repayment term</Label>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {(selectedProvider.tenure_options || [24, 36, 48, 60]).map(months => (
              <Button
                key={months}
                variant={calcInputs.tenure_months === months ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCalcInputs(prev => ({ ...prev, tenure_months: months }))}
              >
                {months} mo
              </Button>
            ))}
          </div>
        </div>
        
        {/* Credit Score Selection */}
        <div>
          <Label>Your credit score</Label>
          <div className="grid grid-cols-1 gap-2 mt-2">
            {CREDIT_SCORES.map(score => (
              <div
                key={score.value}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  calcInputs.credit_score === score.value 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setCalcInputs(prev => ({ ...prev, credit_score: score.value }))}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">{score.label}</span>
                    <p className="text-xs text-gray-500">{score.description}</p>
                  </div>
                  {calcInputs.credit_score === score.value && (
                    <CheckCircle2 className="h-5 w-5 text-blue-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Finance Summary */}
        {calculatingQuote ? (
          <div className="flex items-center justify-center p-6">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <span className="ml-2">Calculating...</span>
          </div>
        ) : calculatedQuote?.calculation && (
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Your Finance Summary
            </h4>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Borrow amount</span>
                <span className="font-medium">{formatCurrency(calculatedQuote.calculation.borrow_amount)}</span>
              </div>
              
              <div className="flex justify-between py-2 border-y border-green-200">
                <span className="font-medium text-green-800">Monthly cost</span>
                <span className="text-2xl font-bold text-green-700">
                  {formatCurrency(calculatedQuote.calculation.monthly_payment)}
                </span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Best available rate</span>
                <span className="font-medium">{calculatedQuote.calculation.apr}% APR</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total cost of credit</span>
                <span className="font-medium">{formatCurrency(calculatedQuote.calculation.total_cost_of_credit)}</span>
              </div>
              
              <div className="flex justify-between text-sm pt-2 border-t border-green-200">
                <span className="font-medium text-gray-700">Total payable (inc. deposit)</span>
                <span className="font-bold">{formatCurrency(calculatedQuote.calculation.total_payable)}</span>
              </div>
            </div>
            
            {/* Representative Example */}
            {calculatedQuote.representative_example && (
              <div className="mt-4 p-3 bg-white/50 rounded text-xs text-gray-600">
                <strong>HP Representative example:</strong> {calculatedQuote.representative_example}
              </div>
            )}
          </div>
        )}
        
        <Button 
          className="w-full bg-green-600 hover:bg-green-700"
          size="lg"
          onClick={() => setDialogTab('apply')}
        >
          Continue to Apply
          <ChevronRight className="h-5 w-5 ml-1" />
        </Button>
      </div>
    );
  };

  // Render Application Tab Content
  const renderApplicationForm = () => (
    <div className="space-y-4">
      {/* Quote Summary (if calculated) */}
      {calculatedQuote?.calculation && (
        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm text-gray-600">Monthly payment</span>
              <div className="text-xl font-bold text-green-700">
                {formatCurrency(calculatedQuote.calculation.monthly_payment)}
              </div>
            </div>
            <div className="text-right">
              <span className="text-sm text-gray-600">for {calcInputs.tenure_months} months</span>
              <div className="text-sm font-medium">@ {calculatedQuote.calculation.apr}% APR</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Vehicle Info */}
      <div className="p-3 bg-blue-50 rounded-lg">
        <div className="font-medium text-blue-900">{listing?.title}</div>
        <div className="text-sm text-blue-700">Price: {formatCurrency(vehicleData.price)}</div>
      </div>
      
      {/* Form Fields */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm text-gray-700">Your Details</h4>
        
        <div>
          <Label htmlFor="app-name">Full Name *</Label>
          <Input
            id="app-name"
            placeholder="Enter your full name"
            value={applicationForm.name}
            onChange={(e) => setApplicationForm(prev => ({ ...prev, name: e.target.value }))}
          />
        </div>
        
        <div>
          <Label htmlFor="app-email">Email *</Label>
          <Input
            id="app-email"
            type="email"
            placeholder="your@email.com"
            value={applicationForm.email}
            onChange={(e) => setApplicationForm(prev => ({ ...prev, email: e.target.value }))}
          />
        </div>
        
        <div>
          <Label htmlFor="app-phone">Phone Number *</Label>
          <Input
            id="app-phone"
            type="tel"
            placeholder="Your phone number"
            value={applicationForm.phone}
            onChange={(e) => setApplicationForm(prev => ({ ...prev, phone: e.target.value }))}
          />
        </div>
        
        <Separator />
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Employment Type</Label>
            <Select
              value={applicationForm.employment_type}
              onValueChange={(v) => setApplicationForm(prev => ({ ...prev, employment_type: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="salaried">Salaried</SelectItem>
                <SelectItem value="self_employed">Self Employed</SelectItem>
                <SelectItem value="business">Business Owner</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="app-income">Monthly Income</Label>
            <Input
              id="app-income"
              type="number"
              placeholder="Optional"
              value={applicationForm.monthly_income}
              onChange={(e) => setApplicationForm(prev => ({ ...prev, monthly_income: e.target.value }))}
            />
          </div>
        </div>
        
        <div>
          <Label>Existing Loans?</Label>
          <RadioGroup
            value={applicationForm.existing_loans}
            onValueChange={(v) => setApplicationForm(prev => ({ ...prev, existing_loans: v }))}
            className="flex gap-4 mt-1"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="loans-no" />
              <Label htmlFor="loans-no" className="font-normal">No</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="loans-yes" />
              <Label htmlFor="loans-yes" className="font-normal">Yes</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
      
      <Alert className="bg-gray-50">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          By submitting, you agree to share your details with {selectedProvider?.name}. 
          They will contact you directly regarding your application.
        </AlertDescription>
      </Alert>
      
      <Button 
        onClick={handleSubmitApplication}
        disabled={submitting}
        className="w-full bg-green-600 hover:bg-green-700"
        size="lg"
        data-testid="submit-finance-application"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Submit Application
          </>
        )}
      </Button>
    </div>
  );

  // Render advanced filters
  const renderAdvancedFilters = () => (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Advanced Filters
        </h4>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="h-3 w-3 mr-1" />
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Interest Level</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {['low', 'medium', 'high'].map(level => (
              <Button
                key={level}
                variant={advancedFilters.interest_level.includes(level) ? 'default' : 'outline'}
                size="sm"
                className="text-xs"
                onClick={() => setAdvancedFilters(prev => ({
                  ...prev,
                  interest_level: prev.interest_level.includes(level)
                    ? prev.interest_level.filter(l => l !== level)
                    : [...prev.interest_level, level]
                }))}
              >
                {level}
              </Button>
            ))}
          </div>
        </div>
        
        <div>
          <Label className="text-xs">Provider Type</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {['bank', 'finance_company'].map(type => (
              <Button
                key={type}
                variant={advancedFilters.provider_type.includes(type) ? 'default' : 'outline'}
                size="sm"
                className="text-xs"
                onClick={() => setAdvancedFilters(prev => ({
                  ...prev,
                  provider_type: prev.provider_type.includes(type)
                    ? prev.provider_type.filter(t => t !== type)
                    : [...prev.provider_type, type]
                }))}
              >
                {type.replace('_', ' ')}
              </Button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Checkbox
          id="regulated-only"
          checked={advancedFilters.is_regulated === true}
          onCheckedChange={(checked) => setAdvancedFilters(prev => ({
            ...prev,
            is_regulated: checked ? true : null
          }))}
        />
        <Label htmlFor="regulated-only" className="text-sm">Regulated lenders only</Label>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading finance options...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6" data-testid="finance-this-car-section">
      <Accordion type="single" collapsible defaultValue="finance">
        <AccordionItem value="finance" className="border-0">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                <Banknote className="h-5 w-5 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold">Finance this Car</h3>
                <p className="text-sm text-gray-500 font-normal">
                  Compare finance options from trusted lenders
                </p>
              </div>
              {isDemo && (
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200 ml-2" data-testid="finance-demo-badge">
                  DEMO
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          
          <AccordionContent className="px-6 pb-6">
            {/* Quick Filter Badges */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {categories.map(category => {
                  const isSelected = selectedBadges.includes(category.id);
                  const CategoryIcon = BADGE_ICONS[category.id] || Star;
                  return (
                    <Button
                      key={category.id}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleBadge(category.id)}
                      className={isSelected ? 'bg-blue-600' : ''}
                      data-testid={`filter-badge-${category.id}`}
                    >
                      <CategoryIcon className="h-4 w-4 mr-1" />
                      {category.label}
                    </Button>
                  );
                })}
              </div>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-4 mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              <div className="flex items-center gap-1 text-green-700">
                <Building2 className="h-4 w-4" />
                <span>Trusted Lenders</span>
              </div>
              <div className="flex items-center gap-1 text-blue-700">
                <Car className="h-4 w-4" />
                <span>Used Cars Accepted</span>
              </div>
              <div className="flex items-center gap-1 text-purple-700">
                <Shield className="h-4 w-4" />
                <span>Regulated</span>
              </div>
            </div>
            
            {/* Advanced Filters Toggle */}
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="w-full justify-between"
              >
                <span className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Refine Filters
                  {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>
            
            {showAdvancedFilters && renderAdvancedFilters()}
            
            {/* Providers List */}
            {providers.length > 0 ? (
              <div className="space-y-4 mt-4">
                {providers.map(provider => renderProviderCard(provider))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                <p>No finance providers match your criteria.</p>
                {activeFilterCount > 0 && (
                  <Button variant="link" onClick={clearAllFilters} className="mt-2">
                    Clear filters
                  </Button>
                )}
              </div>
            )}
            
            {/* Disclaimer */}
            {disclaimer && (
              <Alert className="mt-4 bg-gray-50 border-gray-200">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs text-gray-600">
                  {disclaimer.general}
                  {disclaimer.specific && <span className="block mt-1">{disclaimer.specific}</span>}
                </AlertDescription>
              </Alert>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      
      {/* Enhanced Application Dialog with Calculator */}
      <Dialog open={showApplicationDialog} onOpenChange={setShowApplicationDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-green-600" />
              {selectedProvider?.show_calculator !== false ? 'Finance Calculator' : 'Apply for Finance'}
            </DialogTitle>
            <DialogDescription>
              {selectedProvider?.name} - {selectedProvider?.provider_type?.replace('_', ' ')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedProvider?.show_calculator !== false ? (
            <Tabs value={dialogTab} onValueChange={setDialogTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="calculator" className="flex items-center gap-2">
                  <Calculator className="h-4 w-4" />
                  Calculator
                </TabsTrigger>
                <TabsTrigger value="apply" className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Apply
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="calculator" className="mt-4">
                {renderCalculator()}
              </TabsContent>
              
              <TabsContent value="apply" className="mt-4">
                {renderApplicationForm()}
              </TabsContent>
            </Tabs>
          ) : (
            <div className="py-4">
              {renderApplicationForm()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FinanceThisCar;
